import { Avatar, Box, ButtonBase, Card, Typography } from "@mui/material"
import SvgIconStyle from "minimals-template/components/SvgIconStyle"
import { FolderIcon } from "routes/schedule/tree/schedules/folder-icon"
import { useTheme } from "@mui/styles"
import { Else, If } from "lib/@components/switch"
import { ListItemBox } from "lib/@components/ListItemBox"
import { getGroupTilePresentation } from "./group-tile-presentation"
import { useEffect, useRef, useState } from "react"

export function GroupTile({ parentItem, onClick }) {
    const theme = useTheme()
    const [failedImage, setFailedImage] = useState()
    const [loadedImage, setLoadedImage] = useState()
    const imageContainerRef = useRef()
    const presentation = getGroupTilePresentation(parentItem, failedImage, loadedImage, { alwaysTall: true })
    useEffect(checkImageAfterRender, [parentItem.data?.image])

    return (
        <ButtonBase
            color="primary"
            sx={{ flex: presentation.buttonFlex, display: "flex", flexDirection: "column" }}
            aria-label={`Choose ${parentItem.name}`}
            onClick={onClick}
        >
            <Card
                data-cy={`facility-card-${parentItem.name?.toLowerCase().split(" ").join("-")}`}
                sx={{
                    textAlign: "center",
                    cursor: "pointer",
                    width: 1,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    pb: presentation.cardPaddingBottom,
                    transition: presentation.transition,
                }}
            >
                <If truthy={presentation.variant === "image"}>
                    <Box sx={{ position: "relative", width: 1 }} ref={imageContainerRef}>
                        <Box
                            height={presentation.accentLineHeight}
                            overflow="hidden"
                            position="relative"
                            width={"100vw"}
                            bgcolor={presentation.accentColor}
                            sx={{ transition: presentation.transition }}
                        />
                        <SvgIconStyle
                            src="/assets/icons/shape-avatar.svg"
                            sx={{
                                width: 144,
                                height: 62,
                                zIndex: 10,
                                left: 0,
                                right: 0,
                                bottom: -6,
                                mx: "auto",
                                position: "absolute",
                                color: "background.paper",
                                opacity: presentation.imageOpacity,
                                transition: presentation.transition,
                            }}
                        />
                        <Box
                            sx={{
                                height: 24,
                                backgroundColor: theme.palette.background.paper,
                                position: "absolute",
                                left: 0,
                                right: 0,
                                width: "100%",
                                bottom: -4,
                                zIndex: 10,
                                opacity: presentation.imageOpacity,
                                transition: presentation.transition,
                            }}
                        />
                        <Avatar
                            alt={`${parentItem.label} picture`}
                            src={getAvatarSource()}
                            imgProps={{ onError: handleImageError, onLoad: handleImageLoad }}
                            sx={{
                                width: presentation.avatarSize,
                                height: presentation.avatarSize,
                                zIndex: 11,
                                left: 0,
                                right: 0,
                                bottom: -22,
                                mx: "auto",
                                position: "absolute",
                                opacity: presentation.imageOpacity,
                                transition: presentation.transition,
                            }}
                        >
                            <FolderIcon />
                        </Avatar>
                    </Box>
                    <Typography
                        variant="subtitle1"
                        sx={{ mt: presentation.titleMarginTop, transition: presentation.transition }}
                    >
                        {parentItem.label ?? "Not named"}
                    </Typography>
                    <Else>
                        <Box sx={{ height: presentation.accentLineHeight, bgcolor: presentation.accentColor }} />
                        <ListItemBox spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                            <Avatar
                                sx={{
                                    width: presentation.avatarSize,
                                    height: presentation.avatarSize,
                                    bgcolor: "background.neutral",
                                    color: "primary.main",
                                }}
                            >
                                <FolderIcon />
                            </Avatar>
                            <Box sx={{ minWidth: 0, textAlign: "left" }}>
                                <Typography variant="subtitle2">{parentItem.label ?? "Not named"}</Typography>
                                <If truthy={!!parentItem.name && parentItem.name !== parentItem.label}>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        {parentItem.name}
                                    </Typography>
                                </If>
                            </Box>
                        </ListItemBox>
                    </Else>
                </If>
            </Card>
        </ButtonBase>
    )

    function handleImageError() {
        setFailedImage(parentItem.data?.image)
        setLoadedImage()
    }

    function getAvatarSource() {
        if (parentItem.data?.image === failedImage) return undefined
        return parentItem.data?.image
    }

    function handleImageLoad(event) {
        markImageLoadedWhenUsable(event.currentTarget)
    }

    function checkImageAfterRender() {
        if (!parentItem.data?.image) return undefined
        checkLoadedImage()
        const interval = setInterval(checkLoadedImage, 250)
        const timeout = setTimeout(stopCheckingImage, 15000)
        return cleanUpImageCheck

        function stopCheckingImage() {
            clearInterval(interval)
        }

        function cleanUpImageCheck() {
            clearInterval(interval)
            clearTimeout(timeout)
        }
    }

    function checkLoadedImage() {
        const image = imageContainerRef.current?.querySelector("img")
        if (!image?.complete) return
        markImageLoadedWhenUsable(image)
    }

    function markImageLoadedWhenUsable(image) {
        if (image.naturalWidth > 0 && image.naturalHeight > 0) {
            setLoadedImage(parentItem.data?.image)
            return
        }
        setFailedImage(parentItem.data?.image)
        setLoadedImage()
    }
}
